import React, { CSSProperties, Component, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { MdClose } from 'react-icons/md';
import { Virtuoso } from 'react-virtuoso';

import Downshift, { ControllerStateAndHelpers, StateChangeOptions } from 'downshift';
import isEqual from 'fast-deep-equal';
import matchSorter from 'match-sorter';
import removeAccents from 'remove-accents';
import { DropdownOption, DropdownOptionGroup } from 'types';

import { styled } from '../../theme';
import { Color, Size, Status } from '../../utility/common-props-helpers';
import FlexRow from '../FlexRow';
import BaseInput from '../Input';
import SvgIcon from '../SvgIcon';
import { createOptionsFromGranularity } from './utils';

/**
 * Consts
 */
const MULTISELECT_HOTKEY = 'shiftKey';

/**
 * Types
 */
type Value = DropdownOption | DropdownOption[] | null;

const defaultProps = {
    size: 'medium',
    color: 'tertiary',
    filterTopOption: () => true,
};

type SortOptionsOrder = 'asc';

type Props = {
    className?: string;
    id?: string;
    dataTestid?: string;
    style?: CSSProperties;
    size?: Size;
    color?: Color;
    status?: Status;
    autoFocus?: boolean;
    disabled?: boolean;
    isMulti?: boolean;
    isOpen?: boolean;
    isSearchable?: boolean;
    isClearable?: boolean;
    name?: string;
    placeholder?: string;
    options: (DropdownOption | DropdownOptionGroup)[];
    optionsLabel?: string;
    topOptionsLabel?: string;
    multiselectLabel?: string;
    initialValue?: Value;
    value?: Value;
    searchField?: string;
    sortOptionsOrder?: SortOptionsOrder;
    preprocessOptions?: (
        options: (DropdownOption | DropdownOptionGroup)[]
    ) => (DropdownOption | DropdownOptionGroup)[];
    renderOption?: (option: DropdownOption) => ReactNode;
    renderTopOption?: (option: DropdownOption) => ReactNode;
    filterTopOption?: (option: DropdownOption) => boolean;
    onChange?:
        | ((ev: {
              target: {
                  name: any;
                  value: DropdownOption[];
              };
          }) => void)
        | ((ev: {
              target: {
                  name: any;
                  value: DropdownOption;
              };
          }) => void);
    onSelect?: (ev: {
        target: {
            name?: string;
            value: DropdownOption | DropdownOption[];
        };
    }) => void;
    onMultiSelect?: (ev: {
        target: {
            name?: string;
            value: DropdownOption[];
        };
    }) => void;
    onRemove?: (ev: {
        target: {
            name?: string;
            value: DropdownOption;
        };
    }) => void;
    onClear?: () => void;
    onAfterChange?: () => void;
    // forwardedRef?: any;
};

type State = {
    selectedItems: DropdownOption[];
    topOptions: DropdownOption[];
    visibleOptions: DropdownOption[];
    menuPosition: 'bottom' | 'top';
    multiItemSelectionStartIndex: number | null;
};

/**
 * Utils
 */
const ITEM_HEIGHT = 49;
const MAX_HEIGHT = 300;

const defaultRenderOption = (i: DropdownOption) => (i ? i.label : '');

/**
 * Styles
 */
const RootContainer = styled('div')(({ theme }) => ({
    position: 'relative',
    fontFamily: theme.typography.fontFamily.medium,
    fontWeight: theme.typography.fontWeight.base,
    fontSize: '1.6rem',
    outline: 'none',
}));

const InputContainer = styled('div')({
    position: 'relative',
});

const InputFieldContainer = styled(BaseInput, {
    shouldForwardProp: (prop) => !['status'].includes(prop),
})<Pick<Props, 'id' | 'size' | 'status' | 'disabled' | 'color' | 'isSearchable' | 'isMulti'>>(
    {},
    ({ theme, size, status, disabled, color, isSearchable, isMulti }) => ({
        outline: 'none',
        width: '100%',
        padding: '5px 40px 5px 15px',
        cursor: 'text',
        color: theme.color[color + 'Text'],
        border: theme.border[`${color}`] || 'unset',
        background: theme.color[color + '1'],
        ...(isMulti && {
            height: 'auto',
        }),
        ...(!isSearchable && {
            cursor: 'pointer',
        }),
        ...(disabled && {
            opacity: 0.7,
            pointerEvents: 'none',
        }),
        ...(status && {
            border: '1px solid ' + theme.color[status],
        }),
    })
);

const InputField = styled('input')<Pick<Props, never>>(
    {
        background: 'transparent',
        border: 'none',
        height: 30,
        padding: '2px 0 0 0',
        margin: '5px 0',
        font: 'inherit',
        cursor: 'inherit',
    },
    ({ theme }) => ({
        caretColor: theme.color.primary1,
        '::placeholder': {
            color: theme.color.gray40,
        },
    })
);

const SelectedItemContainer = styled('div')<Pick<Props, 'size' | 'isMulti' | 'disabled'>>(
    {
        margin: '5px 10px 5px 0',
        padding: '5px 8px',
        minHeight: 30,
        display: 'inline-block',
    },
    ({ theme, size, isMulti, disabled }) => ({
        ...(isMulti && {
            backgroundColor: '#fff',
            borderRadius: theme.borderRadius.base,
        }),
        ...(disabled && {
            color: theme.color.disabled,
        }),
    })
);

const SelectedItem = styled('div')<Pick<Props, 'size' | 'isMulti'>>(
    {
        // display: 'flex',
        // alignItems: 'center',
    },
    ({ theme, size, isMulti }) => ({
        width: '100%',
        ...(isMulti && {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }),
    })
);

const SelectedItemCloseButton = styled('button')<Pick<Props, 'size'>>(
    {
        cursor: 'pointer',
        border: 'none',
        background: 'transparent',
        padding: '0 0',
        marginLeft: 4,
    },
    ({ theme, size }) => ({})
);

const StyledClearIcon = styled(MdClose)(({ theme }) => ({
    verticalAlign: 'top',
    height: 20,
    marginRight: -2,
    color: theme.color.primary1,
    ':hover, :focus': {
        color: theme.color.red,
    },
    ':active': {
        color: theme.color.primary3,
    },
}));

const ToggleButton = styled('button')<Pick<Props, 'disabled' | 'color'>>(
    ({ theme, disabled, color }) => ({
        color: theme.color[color + 'Text'],
        position: 'absolute',
        padding: 0,
        top: 0,
        right: 18,
        height: '100%',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        ...(disabled && {
            opacity: 0.7,
            pointerEvents: 'none',
        }),
    })
);

const MenuContainer = styled('div')<{
    isOnTop: boolean;
}>(
    ({ theme, isOnTop }) => ({
        zIndex: isOnTop ? theme.zIndex.dropdownMenus : 1, // required to be on top of PageHeader in opened state
        position: 'absolute',
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
    }),
    ({ theme, isOnTop }) => ({
        marginTop: 0,
        background: theme.color.secondary1,
        boxShadow: theme.boxShadow.base1,
        ...(isOnTop
            ? {
                  bottom: '100%',
                  borderRadius: `${theme.borderRadius.base}px ${theme.borderRadius.base}px 0 0`,
              }
            : {
                  top: '100%',
                  borderRadius: `0 0 ${theme.borderRadius.base}px ${theme.borderRadius.base}px`,
              }),
    })
);

const TopOptionList = styled('ul')(({ theme }) => ({
    padding: 0,
}));

const TopOptionLabel = styled('div')(({ theme }) => ({
    margin: '0 30px 0 20px',
    paddingTop: 20,
    paddingBottom: 10,
    fontSize: '1.2rem',
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.color.gray40,
    textTransform: 'uppercase',
}));

const TopOptionFlexRow = styled(FlexRow)(({ theme }) => ({
    borderBottom: '1px solid rgba(162,163,164, 0.3)',
}));

const OptionListItem = styled('li')<{
    isSelected: boolean;
    isHighlighted: boolean;
    isMultiSelectionHighlighted: boolean;
    disabled: boolean;
}>(
    {
        cursor: 'pointer',
        display: 'block',
        padding: '13px 20px',
        minHeight: ITEM_HEIGHT + 'px',
        textTransform: 'none',
        whiteSpace: 'pre-wrap',
        wordWrap: 'normal',
    },
    ({ theme, isSelected, isHighlighted, isMultiSelectionHighlighted, disabled }) => ({
        color: theme.color.jetBlack,
        ...(isSelected && {
            fontWeight: theme.typography.fontWeight.medium,
            fontSize: 'inherit',
            color: theme.color.primary1,
        }),
        ...(isHighlighted && {
            background: '#e9eaea',
        }),
        ...(isMultiSelectionHighlighted && {
            background: '#e9eaea',
        }),
        ...(disabled && {
            color: theme.color.gray40,
            cursor: 'default',
        }),
    })
);

/**
 * Component
 */

/**
 * @deprecated Use Dropdown component instead
 **/

export class Dropdown2 extends Component<Props, State> {
    static createOptionsFromGranularity = createOptionsFromGranularity;
    static defaultProps = defaultProps;

    listRef = React.createRef<typeof Virtuoso>();
    inputRef = React.createRef<HTMLInputElement>();

    mounted = false;

    state: State = {
        visibleOptions: this.getParsedOptions(),
        topOptions: [],
        selectedItems: this.props.isMulti
            ? Array.isArray(this.props.initialValue)
                ? this.props.initialValue
                : []
            : [],
        menuPosition: 'bottom',
        multiItemSelectionStartIndex: null,
    };

    componentDidMount(): void {
        this.mounted = true;
        window.addEventListener('scroll', this.handleScroll, true);
        this.updateMenuPosition();
    }

    componentWillUnmount() {
        this.mounted = false;
        window.removeEventListener('scroll', this.handleScroll);
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const { options } = this.props;
        const filterDepth = (i) => {
            if (i.options != null) {
                return {
                    ...i,
                    options: i.options.map(({ value, label }) => ({ value, label })),
                };
            } else {
                const { value, label } = i;
                return {
                    value,
                    label,
                };
            }
        };

        if (!isEqual(prevProps.options.map(filterDepth), options.map(filterDepth))) {
            this.resetVisibleOptions();
        }

        this.updateMenuPosition();
    }

    handleScroll = () => this.updateMenuPosition();

    // update menu position to be top or bottom
    updateMenuPosition = () => {
        if (!this.mounted) {
            return;
        }

        const node = ReactDOM.findDOMNode(this);
        if (!node) {
            return;
        }

        const rect = node.getBoundingClientRect();
        if (!rect) {
            return;
        }

        const menuPosition = rect.y + 350 > window.innerHeight ? 'top' : 'bottom';

        if (menuPosition !== this.state.menuPosition && this.mounted) {
            this.setState({ menuPosition });
        }
    };

    resetVisibleOptions() {
        this.setState({
            visibleOptions: this.getParsedOptions(),
            topOptions: [],
        });
    }

    getParsedOptions(): DropdownOption[] {
        const { options, preprocessOptions, sortOptionsOrder } = this.props;

        const reduced = options.reduce((prev, i) => {
            if ('options' in i) {
                return prev.concat(i.options);
            }
            return prev.concat(i);
        }, [] as DropdownOption[]);

        const preprocessedOptions = preprocessOptions ? preprocessOptions(reduced) : reduced;
        if (sortOptionsOrder) {
            this.sortOptions(preprocessedOptions);
        }
        return preprocessedOptions;
    }

    sortOptions(options) {
        const { sortOptionsOrder } = this.props;
        let sortingFunction;

        switch (sortOptionsOrder) {
            case 'asc':
            default:
                sortingFunction = (a, b) => a.label.localeCompare(b.label);
        }

        options.sort(sortingFunction);
    }

    updateVisibleOptions = (inputValue?: string | null) => {
        const { searchField = 'label', preprocessOptions } = this.props;

        const options = this.getParsedOptions();
        if (!inputValue) {
            return this.resetVisibleOptions();
        }

        const visibleOptions = options.filter((i) => {
            const searchTerm = removeAccents(inputValue.toLowerCase());
            const itemSearchField = removeAccents(i[searchField].toLowerCase());

            const result = itemSearchField.includes(searchTerm);

            return result;
        });

        // normalize label string for searching
        const normalizeForMatchSorter = (options: DropdownOption[]) =>
            options.map((i) => ({
                ...i,
                [searchField]: i[searchField].trim().replace(/^[\d\.]+\s/, ''),
            }));

        // for each group find top option
        const findTopOption = (options: DropdownOption[]) =>
            matchSorter(options, inputValue, {
                keys: [
                    {
                        threshold: matchSorter.rankings.CONTAINS,
                        key: searchField,
                    },
                ],
            })
                .slice(0, 1)
                .map((i) => {
                    const option = visibleOptions.find(
                        (o) => o.value === i.value
                    ) as DropdownOption;
                    if (option == null) {
                        return undefined;
                    }

                    return {
                        ...option,
                        [searchField]: option[searchField].trim(),
                        index: visibleOptions.findIndex((o) => o.value === i.value),
                    };
                })[0];

        // in each group
        const optionGroups = this.props.options.filter((i): i is DropdownOptionGroup => {
            return 'options' in i;
        });
        //find the top option
        const topOptions = optionGroups
            .map((group) => {
                const groupOptions = preprocessOptions
                    ? preprocessOptions(group.options)
                    : group.options;

                return findTopOption(
                    normalizeForMatchSorter(groupOptions.filter(this.props.filterTopOption))
                );
            })
            .filter(Boolean);

        this.setState({
            visibleOptions,
            topOptions,
        });
    };

    /**
     * Works only with visible items in the list menu
     */
    scrollToItem = (highlightedIndex) => {
        if (this.listRef.current != null) {
            this.listRef.current.scrollToItem(highlightedIndex);
        }
    };

    // /**
    //  * Works only with visible items in the list menu
    //  */
    // scrollTo = (offset) => {
    //     if (this.listRef.current != null) {
    //         this.listRef.current.scrollTo(offset);
    //     }
    // };

    handleUncontrolledComponentUpdate = (
        selectedItem: DropdownOption,
        downshift: ControllerStateAndHelpers<Value>,
        afterUpdate: () => void
    ) => {
        if (this.props.isMulti) {
            if (Array.isArray(this.props.value)) {
                // controlled component
                afterUpdate();
            } else if (selectedItem != null) {
                // uncontrolled component
                const selectedItems = this.state.selectedItems;
                if (selectedItems.includes(selectedItem)) {
                    this.removeSelectedItem(selectedItem, afterUpdate);
                } else {
                    this.addSelectedItems(selectedItem, afterUpdate);
                }
            }
        } else {
            afterUpdate();
        }
    };

    handleSelection = (
        selectedItem: DropdownOption,
        downshift: ControllerStateAndHelpers<Value>
    ) => {
        const { onChange, onSelect, isMulti } = this.props;
        if (isMulti && event && event[MULTISELECT_HOTKEY]) {
            return;
        }

        const afterUpdate = () => {
            onChange &&
                onChange({
                    target: {
                        name: this.props.name,
                        value: selectedItem,
                    },
                });
            onSelect &&
                onSelect({
                    target: {
                        name: this.props.name,
                        value: selectedItem,
                    },
                });
        };

        this.handleUncontrolledComponentUpdate(selectedItem, downshift, afterUpdate);
    };

    handleRemove = (selectedItem, downshift: ControllerStateAndHelpers<Value>) => {
        const { onChange, onRemove } = this.props;

        const afterUpdate = () => {
            onChange &&
                onChange({
                    target: {
                        name: this.props.name,
                        value: selectedItem,
                    },
                });
            onRemove &&
                onRemove({
                    target: {
                        name: this.props.name,
                        value: selectedItem,
                    },
                });
        };

        this.handleUncontrolledComponentUpdate(selectedItem, downshift, afterUpdate);
    };

    getRemoveButtonProps = ({ onClick, item, ...props }: any = {}, downshift) => {
        return {
            onClick: (e) => {
                e.preventDefault();
                onClick && onClick(e);
                e.stopPropagation();
                this.handleRemove(item, downshift);
            },
            ...props,
        };
    };

    clearSelectedItems = () => {
        const afterUpdate = () => {
            const { onClear } = this.props;
            onClear && onClear();
        };
        this.setState({ selectedItems: [] }, afterUpdate);
    };

    removeSelectedItem = (item, cb?) => {
        this.setState(({ selectedItems }) => {
            if (!selectedItems) {
                return null;
            }
            return {
                selectedItems: selectedItems.filter((i) => i !== item),
            };
        }, cb);
    };

    addSelectedItems = (items: any | any[], cb?) => {
        this.setState(({ selectedItems }) => {
            if (!selectedItems) {
                return null;
            }
            return {
                selectedItems: selectedItems.concat(items),
            };
        }, cb);
    };

    stateReducer = (
        state: ControllerStateAndHelpers<Value>,
        changes: StateChangeOptions<Value>
    ) => {
        const { isMulti } = this.props;

        switch (changes.type) {
            // when selected an item with a keyboard or mouse
            case Downshift.stateChangeTypes.keyDownEnter:
            case Downshift.stateChangeTypes.clickItem:
                // preserve open menu
                return {
                    ...changes,
                    inputValue: state.inputValue,
                    isOpen: state.isOpen,
                    highlightedIndex: state.highlightedIndex,
                };

                break;

            // when changed a value prop
            case Downshift.stateChangeTypes.controlledPropUpdatedSelectedItem:
                if (isMulti) {
                    // in multi selection preserve
                    return {
                        ...changes,
                        inputValue: state.inputValue,
                    };
                } else {
                    // in single selection close dropdown on value change
                    return {
                        ...changes,
                        inputValue: undefined,
                        isOpen: false,
                    };
                }
                break;
        }

        // when dropdown is closed
        if (changes.hasOwnProperty('isOpen') && !changes.isOpen) {
            // in multi selection
            return {
                ...changes,
                inputValue: undefined,
            };
        }

        return changes;
    };

    handleStateChange = (
        changes: StateChangeOptions<Value>,
        downshift: ControllerStateAndHelpers<Value>
    ) => {
        const { isMulti, onAfterChange } = this.props;

        // when user input changed
        if (changes.hasOwnProperty('inputValue')) {
            if (changes.inputValue) {
                this.updateVisibleOptions(changes.inputValue);
            } else {
                this.resetVisibleOptions();
            }
        }

        // when dropdown is opened
        if (changes.hasOwnProperty('isOpen') && changes.isOpen) {
            this.resetVisibleOptions();
        }

        // when dropdown is closed
        if (changes.hasOwnProperty('isOpen') && !changes.isOpen) {
            this.setState({ multiItemSelectionStartIndex: null });
            onAfterChange && onAfterChange();
        }

        // handle scrolling when highlightedIndex has changed
        // if (changes.hasOwnProperty('highlightedIndex') && changes.highlightedIndex != null) {
        // this.scrollToItem(change.highlightedIndex);
        // }
    };

    handleFocus = (event, downshift: ControllerStateAndHelpers<Value>) => {
        downshift.openMenu();
        this.inputRef.current && this.inputRef.current.select();
    };

    isOptionEqual = (selectedOption: { value }, option: { value }): boolean => {
        if (selectedOption == null || option == null) {
            return selectedOption === option;
        }

        const getValue = (option) =>
            option.value != null ? option.value.toString() : option.value;

        return getValue(selectedOption) === getValue(option);
    };

    render() {
        const { onChange, onSelect, initialValue, options, value, isMulti, ...restProps } =
            this.props;
        const { visibleOptions } = this.state;

        return (
            <Downshift
                itemToString={defaultRenderOption}
                itemCount={visibleOptions.length}
                onStateChange={this.handleStateChange}
                onSelect={this.handleSelection}
                initialSelectedItem={isMulti ? undefined : initialValue}
                selectedItem={value}
                stateReducer={this.stateReducer}
                initialInputValue={''}
                {...restProps}
            >
                {this.renderChildren}
            </Downshift>
        );
    }

    renderChildren = (downshift: ControllerStateAndHelpers<Value>) => {
        const {
            autoFocus,
            status,
            size,
            color,
            isClearable,
            isSearchable,
            isMulti,
            placeholder,
            disabled,
            name,
            renderOption,
            renderTopOption,
            value,
            optionsLabel,
            topOptionsLabel,
            multiselectLabel,
            style,
            id,
            dataTestid,
        } = this.props;
        const { topOptions, selectedItems } = this.state;

        const {
            getRootProps,
            getLabelProps,
            getInputProps,
            getToggleButtonProps,
            getItemProps,
            isOpen,
            toggleMenu,
            clearSelection,
            selectedItem,
            highlightedIndex,
            inputValue = '',
        } = downshift;

        let { visibleOptions } = this.state;

        if (visibleOptions.length < 1) {
            visibleOptions = [
                {
                    value: null,
                    label: 'No options',
                    disabled: true,
                },
            ];
        }

        const selectedItemsRaw = isMulti
            ? Array.isArray(value)
                ? value
                : selectedItems
            : selectedItem == null
              ? []
              : Array.isArray(selectedItem)
                ? selectedItem
                : [selectedItem];

        const _selectedItems = selectedItemsRaw.filter(Boolean);

        const isMenuOnTop = this.state.menuPosition === 'top';

        return (
            <RootContainer {...getRootProps()} style={style} tabIndex="0">
                <InputContainer>
                    <InputFieldContainer
                        id={id}
                        data-testid={dataTestid}
                        as="div"
                        {...{
                            size,
                            status,
                            disabled,
                            color,
                            isSearchable,
                            isMulti,
                            onClick: (ev) =>
                                isSearchable ? this.handleFocus(ev, downshift) : toggleMenu(ev),
                        }}
                    >
                        {isSearchable ? (
                            <InputField
                                {...getInputProps({
                                    ref: this.inputRef,
                                    size: placeholder != null ? placeholder.length : 20,
                                    isOpen,
                                    autoFocus,
                                    disabled,
                                    placeholder,
                                    name,
                                    value:
                                        inputValue && inputValue.trimStart
                                            ? inputValue.trimStart()
                                            : inputValue || '',
                                    readOnly: !isSearchable,
                                })}
                            />
                        ) : !_selectedItems.length ? (
                            <SelectedItemContainer disabled>{placeholder}</SelectedItemContainer>
                        ) : (
                            ''
                        )}
                        {_selectedItems.map((item) => {
                            return (
                                <SelectedItemContainer
                                    key={item.value}
                                    isMulti={isMulti}
                                    disabled={item.disabled}
                                >
                                    <SelectedItem>
                                        {item.label}
                                        {isMulti && (
                                            <SelectedItemCloseButton
                                                {...this.getRemoveButtonProps({ item }, downshift)}
                                            >
                                                <StyledClearIcon />
                                            </SelectedItemCloseButton>
                                        )}
                                    </SelectedItem>
                                </SelectedItemContainer>
                            );
                        })}
                    </InputFieldContainer>
                    {isClearable && _selectedItems.length > 0 ? (
                        <ToggleButton
                            color={color}
                            onClick={() => {
                                clearSelection();
                                isMulti && this.clearSelectedItems();
                            }}
                            aria-label="clear selection"
                            disabled={disabled}
                        >
                            <StyledClearIcon />
                        </ToggleButton>
                    ) : (
                        <ToggleButton
                            {...getToggleButtonProps({
                                disabled,
                                color,
                            })}
                        >
                            <SvgIcon
                                alt="Down Icon"
                                src={isOpen ? '/icons/ic-arrow-up.svg' : '/icons/ic-arrow-down.svg'}
                                color={(theme) => theme.color.gray40}
                                data-testid="dropdown-down-chevron"
                            />
                        </ToggleButton>
                    )}
                </InputContainer>
                <MenuContainer isOnTop={isMenuOnTop}>
                    {!isOpen || topOptions.length === 0 ? null : (
                        <TopOptionList>
                            {topOptionsLabel && (
                                <TopOptionFlexRow justify="space-between">
                                    <TopOptionLabel>{topOptionsLabel}</TopOptionLabel>
                                </TopOptionFlexRow>
                            )}
                            {topOptions.map((item) => (
                                <OptionListItem
                                    style={{ padding: '12px 20px 0 40px' }}
                                    {...getItemProps({
                                        item,
                                        key: 'top' + item.index,
                                        index: item.index,
                                        disabled: item.disabled,
                                        isSelected: isMulti
                                            ? _selectedItems
                                                  .map((item) => item.value)
                                                  .includes(item.value)
                                            : this.isOptionEqual(selectedItem, item),
                                        isHighlighted: highlightedIndex === item.index,
                                    })}
                                >
                                    {renderTopOption
                                        ? renderTopOption(item)
                                        : defaultRenderOption(item)}
                                </OptionListItem>
                            ))}
                        </TopOptionList>
                    )}

                    {!isOpen || visibleOptions.length === 0 ? null : (
                        <>
                            {(optionsLabel || (isMulti && multiselectLabel)) && (
                                <TopOptionFlexRow justify="space-between">
                                    <TopOptionLabel>{optionsLabel}</TopOptionLabel>
                                    {isMulti && <TopOptionLabel>{multiselectLabel}</TopOptionLabel>}
                                </TopOptionFlexRow>
                            )}
                            <Virtuoso
                                style={{
                                    height:
                                        visibleOptions.length * ITEM_HEIGHT > MAX_HEIGHT
                                            ? MAX_HEIGHT
                                            : visibleOptions.length * ITEM_HEIGHT,
                                }}
                                data={visibleOptions}
                                itemContent={(index, option) => {
                                    const { onMultiSelect, value, name } = this.props;
                                    const { multiItemSelectionStartIndex } = this.state;
                                    const isItemSelected = isMulti
                                        ? !!_selectedItems.find((el) =>
                                              this.isOptionEqual(el, option)
                                          )
                                        : this.isOptionEqual(selectedItem, option);

                                    return (
                                        <div
                                            {...{
                                                // ref,
                                                style,
                                                disabled: option.disabled,
                                            }}
                                        >
                                            <OptionListItem
                                                {...getItemProps({
                                                    item: option,
                                                    index,
                                                    disabled: option.disabled,
                                                    isSelected: isItemSelected,
                                                    isHighlighted: highlightedIndex === index,
                                                    isMultiSelectionHighlighted:
                                                        multiItemSelectionStartIndex === index,
                                                    onClick: (event) => {
                                                        const handleKeyUp = (ev) => {
                                                            if (!ev[MULTISELECT_HOTKEY]) {
                                                                document.removeEventListener(
                                                                    'keyup',
                                                                    handleKeyUp
                                                                );
                                                                this.setState({
                                                                    multiItemSelectionStartIndex:
                                                                        null,
                                                                });
                                                            }
                                                        };

                                                        const hotkeyPressed =
                                                            !!event[MULTISELECT_HOTKEY];

                                                        if (!isItemSelected && !hotkeyPressed) {
                                                            this.setState({
                                                                multiItemSelectionStartIndex: index,
                                                            });
                                                        } else {
                                                            this.setState({
                                                                multiItemSelectionStartIndex: null,
                                                            });
                                                        }

                                                        if (
                                                            hotkeyPressed &&
                                                            multiItemSelectionStartIndex != null
                                                        ) {
                                                            // add selected items from multiItemSelectionStartIndex to index
                                                            const items =
                                                                multiItemSelectionStartIndex >=
                                                                index
                                                                    ? visibleOptions.slice(
                                                                          index,
                                                                          multiItemSelectionStartIndex +
                                                                              1
                                                                      )
                                                                    : visibleOptions.slice(
                                                                          multiItemSelectionStartIndex,
                                                                          index + 1
                                                                      );

                                                            // controlled component
                                                            if (value && Array.isArray(value)) {
                                                                const verifiedItems = items.filter(
                                                                    (item) => !value.includes(item)
                                                                );

                                                                onMultiSelect &&
                                                                    onMultiSelect({
                                                                        target: {
                                                                            name: name,
                                                                            value: verifiedItems,
                                                                        },
                                                                    });
                                                            }
                                                            // uncontrolled component
                                                            else {
                                                                const verifiedItems = items.filter(
                                                                    (item) => {
                                                                        const itemIsNotSelected =
                                                                            selectedItems.find(
                                                                                (i) =>
                                                                                    i.value ===
                                                                                    item.value
                                                                            ) == null;

                                                                        return itemIsNotSelected;
                                                                    }
                                                                );

                                                                this.addSelectedItems(
                                                                    verifiedItems
                                                                );
                                                            }

                                                            this.setState({
                                                                multiItemSelectionStartIndex: index,
                                                            });
                                                        }
                                                    },
                                                })}
                                            >
                                                {renderOption
                                                    ? renderOption(option)
                                                    : defaultRenderOption(option)}
                                            </OptionListItem>
                                        </div>
                                    );
                                }}
                            />
                        </>
                    )}
                </MenuContainer>
            </RootContainer>
        );
    };
}

export default Dropdown2;
